import React from 'react';

type UseCredentialFormParams = {
    isOpen: boolean;
    validate: (value: string) => boolean;
    /** Optional input sanitiser (e.g. digits-only for a PIN). */
    sanitize?: (value: string) => string;
    onSubmit: (value: string) => void;
};

export type UseCredentialFormResult = {
    value: string;
    isValid: boolean;
    onChange: (value: string) => void;
    onSubmit: () => void;
};

/**
 * Single-field form state shared by the Set-PIN and Reset-Password modals:
 * holds one string, resets when the modal (re)opens, gates submit on the
 * supplied validator, and optionally sanitises input as it's typed.
 */
export const useCredentialForm = ({
    isOpen,
    validate,
    sanitize,
    onSubmit,
}: UseCredentialFormParams): UseCredentialFormResult => {
    const [value, setValue] = React.useState('');

    React.useEffect(() => {
        if (isOpen) {
            setValue('');
        }
    }, [isOpen]);

    const onChange = React.useCallback(
        (next: string) => setValue(sanitize ? sanitize(next) : next),
        [sanitize],
    );

    const isValid = validate(value);

    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit(value);
    }, [isValid, onSubmit, value]);

    return { value, isValid, onChange, onSubmit: onSubmitClick };
};
